/*
 * The price chart.
 *
 * lightweight-charts, which is TradingView's own renderer — a canvas, not
 * SVG, so twelve thousand bars pan and zoom without dropping a frame. That
 * matters here: a day chart of a year is 250 bars, but a one-minute chart of
 * three days is over a thousand and an SVG of that is visibly slow to drag.
 *
 * HOW THE LIVE PRICE GETS ONTO IT
 *
 * Candles are fetched once. Ticks then extend the newest bar in place —
 * `update()` on the same timestamp replaces that bar rather than appending
 * one — so the rightmost candle grows and its wick stretches exactly as it
 * does in a real terminal, without refetching anything. When the clock passes
 * the end of the bar's interval, a new one is opened at the tick's price.
 *
 * This is why the chart does not poll. A five-minute chart gains one closed
 * bar every five minutes; everything between is the last bar moving, and the
 * socket already carries that.
 *
 * The chart is imperative and React is not, so the whole thing lives in refs
 * and is driven by effects. Re-creating it on every render would throw away
 * the reader's pan and zoom, which is the fastest way to make a chart useless.
 */

import {
  ColorType,
  CrosshairMode,
  createChart,
  type CandlestickData,
  type IChartApi,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from "lightweight-charts";
import { useEffect, useRef, useState } from "react";

import { useLive, useWatch } from "../api/stream";
import type { Bar, Interval } from "../api/types";
import { price as fmtPrice, compactNumber } from "../lib/format";

/** Seconds per bar, for deciding when a tick opens a new candle. */
const SECONDS: Record<Interval, number> = {
  minute: 60,
  "3minute": 180,
  "5minute": 300,
  "10minute": 600,
  "15minute": 900,
  "30minute": 1800,
  "60minute": 3600,
  day: 86400,
};

const UP = "#2fd07a";
const DOWN = "#ff4d6a";

interface Props {
  token: number;
  bars: Bar[];
  interval: Interval;
  height?: number;
  /** Draws a dashed line at each — an entry, a stop, a target. */
  markers?: { price: number; label: string; colour?: string }[];
}

export function CandleChart({ token, bars, interval, height = 420, markers = [] }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const candles = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volume = useRef<ISeriesApi<"Histogram"> | null>(null);
  const lastBar = useRef<CandlestickData<Time> | null>(null);

  const live = useLive();
  useWatch([token]);

  const [hover, setHover] = useState<Bar | null>(null);

  /* ── build once ── */
  useEffect(() => {
    if (!host.current) return;

    const c = createChart(host.current, {
      height,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: "#94a1b8",
        fontFamily:
          '"SF Mono", "JetBrains Mono", ui-monospace, Menlo, Consolas, monospace',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(30, 38, 55, 0.7)" },
        horzLines: { color: "rgba(30, 38, 55, 0.7)" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: "#3a465c", width: 1, style: 3, labelBackgroundColor: "#1c2434" },
        horzLine: { color: "#3a465c", width: 1, style: 3, labelBackgroundColor: "#1c2434" },
      },
      rightPriceScale: {
        borderColor: "#1e2637",
        // Room underneath for the volume histogram, which is drawn on its own
        // scale in the bottom fifth.
        scaleMargins: { top: 0.08, bottom: 0.26 },
      },
      timeScale: {
        borderColor: "#1e2637",
        timeVisible: interval !== "day",
        secondsVisible: false,
        rightOffset: 4,
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true },
      handleScale: { mouseWheel: true, pinch: true },
      autoSize: true,
    });

    const candleSeries = c.addCandlestickSeries({
      upColor: UP,
      downColor: DOWN,
      wickUpColor: UP,
      wickDownColor: DOWN,
      borderVisible: false,
      priceFormat: { type: "price", precision: 2, minMove: 0.05 },
    });

    const volumeSeries = c.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });
    c.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.8, bottom: 0 },
      visible: false,
    });

    // The legend follows the crosshair; away from the chart it shows the last
    // bar, which is what the eye expects when the pointer leaves.
    c.subscribeCrosshairMove((param) => {
      const data = param.seriesData.get(candleSeries) as CandlestickData<Time> | undefined;
      if (!data || param.time === undefined) {
        setHover(null);
        return;
      }
      setHover({
        time: Number(param.time),
        open: data.open,
        high: data.high,
        low: data.low,
        close: data.close,
        volume: 0,
      });
    });

    chart.current = c;
    candles.current = candleSeries;
    volume.current = volumeSeries;

    return () => {
      c.remove();
      chart.current = null;
      candles.current = null;
      volume.current = null;
      lastBar.current = null;
    };
  }, [height, interval]);

  /* ── the series ── */
  useEffect(() => {
    if (!candles.current || !volume.current) return;

    const data: CandlestickData<Time>[] = bars.map((b) => ({
      time: b.time as UTCTimestamp,
      open: b.open,
      high: b.high,
      low: b.low,
      close: b.close,
    }));
    candles.current.setData(data);
    volume.current.setData(
      bars.map((b) => ({
        time: b.time as UTCTimestamp,
        value: b.volume,
        color: b.close >= b.open ? "rgba(47,208,122,0.28)" : "rgba(255,77,106,0.28)",
      })),
    );
    lastBar.current = data.length ? { ...data[data.length - 1] } : null;
    chart.current?.timeScale().fitContent();
  }, [bars]);

  /* ── the lines ── */
  useEffect(() => {
    const series = candles.current;
    if (!series) return;
    const lines = markers.map((m) =>
      series.createPriceLine({
        price: m.price,
        color: m.colour ?? "#4d8dff",
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: m.label,
      }),
    );
    return () => lines.forEach((l) => series.removePriceLine(l));
    // The prices themselves are the dependency: a stop that moves should
    // redraw, and re-running on a new array identity every render would not.
  }, [markers.map((m) => `${m.label}:${m.price}`).join("|")]);

  /* ── the live edge ── */
  useEffect(() => {
    const series = candles.current;
    const quote = live.quote(token);
    if (!series || !quote || !lastBar.current || live.stale) return;

    const span = SECONDS[interval];
    const lastTime = Number(lastBar.current.time);
    const nowSec = Math.floor(Date.now() / 1000);

    if (nowSec >= lastTime + span) {
      // The bar this tick belongs to has not been drawn yet. Open it at the
      // tick's own price — a bar that opens where the last one closed would
      // hide the gap, and a gap is worth seeing.
      const opensAt = lastTime + Math.floor((nowSec - lastTime) / span) * span;
      lastBar.current = {
        time: opensAt as UTCTimestamp,
        open: quote.last,
        high: quote.last,
        low: quote.last,
        close: quote.last,
      };
    } else {
      lastBar.current = {
        ...lastBar.current,
        high: Math.max(lastBar.current.high, quote.last),
        low: Math.min(lastBar.current.low, quote.last),
        close: quote.last,
      };
    }
    series.update(lastBar.current);
  }, [live.version, live.stale, token, interval]);

  const shown = hover ?? (bars.length ? bars[bars.length - 1] : null);
  const rising = shown ? shown.close >= shown.open : true;

  return (
    <div className="chart-host">
      {shown && (
        <div className="chart-legend">
          <span>O <b className={rising ? "up" : "down"}>{fmtPrice(shown.open)}</b></span>
          <span>H <b className={rising ? "up" : "down"}>{fmtPrice(shown.high)}</b></span>
          <span>L <b className={rising ? "up" : "down"}>{fmtPrice(shown.low)}</b></span>
          <span>C <b className={rising ? "up" : "down"}>{fmtPrice(shown.close)}</b></span>
          {!hover && shown.volume > 0 && <span>Vol <b>{compactNumber(shown.volume)}</b></span>}
        </div>
      )}
      <div ref={host} style={{ height }} />
    </div>
  );
}
